import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import config from 'react-global-configuration';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import { CoreLink, Menu } from '@uncinc/react-drupal-core';
import { Button, Container } from '@uncinc/uncinc-react-kitchen-sink';

import { Logo, Hamburger, SearchAllContent } from '../../components';
import { createQueryUrl } from '../../components/Search/helpers';
import WebshopLinks from '../../components/WebshopLinks';
import CartPopup from '../../containers/Cart/CartPopup';
import {
  DOMAIN_KVNL,
  DOMAIN_VNL,
  SEARCHKIT_AUTOSUGGEST_HITS,
  SEARCHKIT_INDEX_ALL_CONTENT,
  SEARCH_PATH,
} from '../../config/constants';

import {
  NavigationMainMenuDesktop,
  NavigationMainMenuMobile,
  NavigationSecondaryMenu,
  LegacyNavigationMainMenuDesktop,
  LegacyNavigationMainMenuMobile,
} from '..';

import './style.scss';
import AccountButton from '../../components/AccountButton';

const Navigation = () => {
  const currentDomain = config.get('CURRENT_DOMAIN') as string;
  const { t } = useTranslation();
  const [showCartPopup, setShowCartPopup] = useState(false);
  const [searchBarVisible, setSearchBarVisible] = useState(false);
  const [openMobileMenu, setOpenMobileMenu] = useState(false);
  const [closeAllModals, setCloseAllModals] = useState(0);
  const menuIdSuffix = currentDomain !== DOMAIN_VNL ? `-${currentDomain}` : '';
  const location = useLocation();
  const history = useHistory();
  const isSearchPath = location.pathname === SEARCH_PATH;

  useEffect(() => {
    setSearchBarVisible(false);
  }, [location]);

  const toggleMobileMenu = () => {
    setOpenMobileMenu(!openMobileMenu);
  };

  const toggleSearchOverlay = () => {
    if (isSearchPath) {
      const barContainer = document.body.querySelector('.Searchkit__SearchBar');

      if (barContainer) {
        barContainer.querySelector('input')?.focus();
      }
    } else {
      setSearchBarVisible((state) => !state);
    }
  };

  useEffect(() => {
    if (openMobileMenu) {
      document.body.classList.add('Navigation-wrapper-body');
      setSearchBarVisible(false);
    } else {
      document.body.classList.remove('Navigation-wrapper-body');
    }
  }, [openMobileMenu]);

  const goToUrl = useCallback((url: string) => {
    history.push(url);
  }, [history]);

  const navWrapperClassName = classNames('Navigation-wrapper', {
    'Navigation-wrapper-mobile-show': openMobileMenu === true,
    'Navigation-wrapper-no-shadow': searchBarVisible,
  });

  return (
    <div className={navWrapperClassName}>
      <Container className="Top-navigation__container">
        <div className="Top-navigation__content">
          {config.get('CURRENT_DOMAIN') === DOMAIN_VNL && (
            <div className="Top-navigation__content__webshoplinks">
              <WebshopLinks setShowCartPopup={setShowCartPopup} />
            </div>
          )}
          <Menu id={`simple-links-navigation${menuIdSuffix}`}>
            {(items: any) => (
              <ul className="simple-links-navigation__items">
                {items.map((item: any) => {
                  const title = item.getIn(['link', 'title'], 'Untitled');
                  const url = item.getIn(['link', 'url'], '');
                  return (
                    <li key={title} className="simple-links-navigation__item">
                      <CoreLink href={url}>{title}</CoreLink>
                    </li>
                  );
                })}
              </ul>
            )}
          </Menu>
          {showCartPopup && (
            <CartPopup
              mobile={openMobileMenu}
              setShowCartPopup={setShowCartPopup}
            />
          )}
        </div>
      </Container>
      <Container
        className={classNames('Navigation-container ', {
          'Navigation-mobile-show': openMobileMenu === true,
        })}
      >
        <Logo />
        <div className="kvnl-separator">
          <div className="menu-wrapper">
            {currentDomain === DOMAIN_KVNL && (
              <Menu id={`main${menuIdSuffix}`}>
                {(items: any) => (
                  <LegacyNavigationMainMenuDesktop
                    items={items}
                  />
                )}
              </Menu>
            )}
            {currentDomain === DOMAIN_VNL && (
              <>
                <Menu id="main-workfields">
                  {(items: any) => (
                    <NavigationMainMenuDesktop
                      items={items}
                    />
                  )}
                </Menu>
                <div className="WebshopLinks__wrapper">
                  <WebshopLinks setShowCartPopup={setShowCartPopup} />
                </div>
              </>
            )}
          </div>
          <div className="secondary-menu-wrapper">
            <Menu id={`secondary-navigation${menuIdSuffix}`}>
              {(items: any) => (
                <NavigationSecondaryMenu items={items} />
              )}
            </Menu>
          </div>
          <div className="secondary-menu-wrapper__search">
            <Button
              primary={false}
              icon="search"
              variant="icon-only"
              ariaLabel={t('buttons.search')}
              className={`SearchBar__button__wrapper ${isSearchPath ? 'disabled' : ''}`}
              onClick={toggleSearchOverlay}
            >
              <i className={`SearchBar__button icon ${searchBarVisible ? 'icon-cross' : 'icon-search'}`} />
            </Button>
          </div>
          {currentDomain === DOMAIN_VNL && (
            <AccountButton />
          )}
          {showCartPopup && <CartPopup setShowCartPopup={setShowCartPopup} mobile />}
          <div className="kvnl-separator__hamburgerWrapper">
            <Hamburger
              handleClick={() => {
                setCloseAllModals((x) => x + 1);
                toggleMobileMenu();
              }}
              setShowCartPopup={setShowCartPopup}
              OpenMobileMenu={openMobileMenu}
            />
          </div>
          {currentDomain === DOMAIN_VNL && (
            <Menu id="main-workfields">
              {(items: any) => (
                <NavigationMainMenuMobile
                  closeAllModals={closeAllModals}
                  items={items}
                  handleLinkClick={toggleMobileMenu}
                  setOpenMobileMenu={setOpenMobileMenu}
                />
              )}
            </Menu>
          )}
          {currentDomain === DOMAIN_KVNL && (
            <Menu id={`main${menuIdSuffix}`}>
              {(items: any) => (
                <LegacyNavigationMainMenuMobile
                  closeAllModals={closeAllModals}
                  items={items}
                  handleLinkClick={toggleMobileMenu}
                  setOpenMobileMenu={setOpenMobileMenu}
                />
              )}
            </Menu>
          )}
        </div>
      </Container>

      {/* SearchBar Component */}
      <div className={searchBarVisible ? 'NavigationSearchBar visible' : 'NavigationSearchBar invisible'}>
        <Container>
          <div className="NavigationSearchBar__wrapper">
            <div className="NavigationSearchBar__searchBar">
              {searchBarVisible && (
                <SearchAllContent className="SiteHeader__searchbar" hitsPerPage={SEARCHKIT_AUTOSUGGEST_HITS} analytics>
                  <SearchAllContent.Autocomplete
                    type="secondary"
                    searchBarVisible={searchBarVisible}
                    setSearchBarVisible={setSearchBarVisible}
                    clearOnSearch
                    onSearchSubmit={(query) => {
                      const url = createQueryUrl(SEARCH_PATH, SEARCHKIT_INDEX_ALL_CONTENT, query);
                      goToUrl(url);
                    }}
                    onSuggestionSelected={(suggestion) => {
                      const url = suggestion.autocomplete_text ?
                        createQueryUrl(
                          SEARCH_PATH,
                          SEARCHKIT_INDEX_ALL_CONTENT,
                          suggestion.autocomplete_text[0],
                        ) :
                        suggestion.url[0];

                      goToUrl(url);
                    }}
                  />
                </SearchAllContent>
              )}
            </div>
          </div>
        </Container>
      </div>

    </div>
  );
};

export default Navigation;
